import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import downloadFile from "_shared/_utilities/download-file";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { Masked } from "work/_shared/masked-fields";

const ListContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived: boolean }>(({ waived }) => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  width: "100%",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  overflowY: "auto",
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type ConflictsTabProps = {
  proposal: Proposal;
  commentThreads?: CommentThread[];
  onCommentsClicked: (field: ProposalField, name?: string) => void;
};

export default function ConflictsTab(props: Readonly<ConflictsTabProps>) {
  const { proposal, commentThreads, onCommentsClicked } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const conflictsDocument = await documentApiService.downloadDocument(documentId);
      await downloadFile(conflictsDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download document. Please try again later.", { variant: "error" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (!proposal) return null;

  if (Masked.isMasked(proposal.conflictsCheckWaived) || Masked.isMasked(proposal.conflicts))
    return "You do not have permission to view contracts.";

  return (
    <>
      <HeadControls>
        <FormControlLabel
          control={<Switch disabled={true} checked={proposal?.conflictsCheckWaived.value ?? false} color="primary" />}
          label={
            proposal?.conflictsCheckWaived.value ? (
              <strong>Conflicts Check Waived</strong>
            ) : (
              "Conflicts Check Not Waived"
            )
          }
        />
        <CommentsButton
          commentThreads={commentThreads}
          hideUnreadCount={true}
          field={ProposalField.Conflicts}
          muted
          toolTip={
            commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.Conflicts))
              ? "Comments"
              : "No Comments"
          }
          onCommentsClicked={
            onCommentsClicked
              ? () => {
                  onCommentsClicked(ProposalField.Conflicts);
                }
              : undefined
          }
        />
      </HeadControls>
      <ListContainer waived={proposal?.conflictsCheckWaived.value ?? false} container direction="row">
        <Grid>
          {proposal.conflicts.length === 0 && (
            <HelperText>No conflicts documents</HelperText>
          )}
          {proposal.conflicts.length > 0 && (
            <DocumentList
              documents={proposal?.conflicts ?? []}
              documentType={WorkDocumentType.Conflicts}
              downloadingFileId={isDownloadingFile}
              commentThreads={commentThreads}
              onCommentsClicked={onCommentsClicked}
              disableCommentsPane={true}
              onDownload={handleDownloadDocumentById}
            />
          )}
        </Grid>
      </ListContainer>
    </>
  );
}
